import { Suspense, lazy, useEffect, useMemo } from "react";
import { Outlet } from "react-router-dom";
import {
  ThemeProvider,
  createTheme
} from "@mui/material/styles";
import { Box, Stack } from "@mui/material";
import * as coreLocales from "@mui/material/locale";
import * as gridLocales from "@mui/x-data-grid/locales";
import * as pickersLocales from "@mui/x-date-pickers/locales";
import { useQuery } from "@tanstack/react-query";
import { toDayjsFormat } from "@freyr/freyr-react-common";
import { useGlobalData } from "../hooks/useGlobalData";
import { DEFAULT_LANGUAGE, LANGUAGEMAP, VALID_LANGUAGES } from "../constants";
import {
  DEFAULT_REGIONAL_DATE_SETTINGS,
  Language,
  useConfigStore,
} from "../store/config";
import "../assets/scss/page/index.scss";
import { useUrfReceiveEvents } from "../hooks/urfEvents";
import Loading from "../component/Loading";
import { getUserSettings, postRelatedResource } from "../api/uam";
import usePermissionStore from "../store/permission";
import { getCurrentTenantId } from "../utils/tenant";

const FreyrLibraryProvider = lazy(() =>
  import("@freyr/freyr-react-common").then((m) => ({ default: m.FreyrLibraryProvider }))
);

type SupportedLocales = keyof typeof coreLocales;
type GridLocales = keyof typeof gridLocales;
type PickersLocales = keyof typeof pickersLocales;

const Layouts = () => {
  const { userId, token, tenant } = useGlobalData();
  const {
    language,
    updateLanguage,
    updateRegionalSettings,
    timeZone,
    dateFormat,
    timeFormat,
  } = useConfigStore();
  const themeWithLocale = useMemo(() => {
    const locale: SupportedLocales = LANGUAGEMAP.mui[language];
    const gridLocale: GridLocales = LANGUAGEMAP.mui[language];
    const pickersLocale: PickersLocales = LANGUAGEMAP.mui[language];
    return createTheme(
      {
        palette: {},
      },
      coreLocales[locale],
      gridLocales[gridLocale],
      pickersLocales[pickersLocale]
    );
  }, [language]);

  const {
    isReady,
    setLoading,
    setPermissions,
    setReady,
    resetPermissions,
  } = usePermissionStore((state) => ({
    isReady: state.isReady,
    setLoading: state.setLoading,
    setPermissions: state.setPermissions,
    setReady: state.setReady,
    resetPermissions: state.resetPermissions,
  }));

  useEffect(() => {
    let active = true;

    if (!userId) {
      resetPermissions();
      setReady(true);
      return;
    }

    const loadPermissions = async () => {
      setReady(false);
      setLoading(true);
      setPermissions([]);
      try {
        const response = await postRelatedResource(userId);
        if (!active) return;
        const data = response?.data ?? [];
        const names = data
          .map((item) => item.name)
          .filter((name): name is string => Boolean(name));
        setPermissions(names);
      } catch (error) {
        console.error("Failed to load permissions", error);
      } finally {
        if (active) {
          setLoading(false);
          setReady(true);
        }
      }
    };

    loadPermissions();

    return () => {
      active = false;
    };
  }, [userId, resetPermissions, setLoading, setPermissions, setReady]);

  const { data: userSettings } = useQuery({
    queryKey: ["fetchUserSettings"],
    queryFn: () => getUserSettings(),
    enabled: Boolean(userId),
    retry: false,
    refetchOnWindowFocus: false,
    select: (response) => response?.data,
  });

  useEffect(() => {
    if (!userSettings) {
      return;
    }
    updateRegionalSettings({
      timeZone: userSettings.timeZone || DEFAULT_REGIONAL_DATE_SETTINGS.timeZone,
      dateFormat: userSettings.dateFormat
        ? toDayjsFormat(userSettings.dateFormat)
        : DEFAULT_REGIONAL_DATE_SETTINGS.dateFormat,
      timeFormat: userSettings.timeFormat
        ? toDayjsFormat(userSettings.timeFormat)
        : DEFAULT_REGIONAL_DATE_SETTINGS.timeFormat,
    });
    if (
      userSettings.language &&
      VALID_LANGUAGES.includes(userSettings.language)
    ) {
      updateLanguage(userSettings.language as Language);
    } else {
      updateLanguage(DEFAULT_LANGUAGE);
    }
  }, [userSettings, updateRegionalSettings, updateLanguage]);

  useUrfReceiveEvents();

  if (!isReady) {
    return (
      <ThemeProvider theme={themeWithLocale}>
        <Loading open />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={themeWithLocale}>
      <Suspense fallback={<Loading open />}>
        <FreyrLibraryProvider
          token={token}
          language={language}
          tenant={getCurrentTenantId() ?? tenant}
          regionalSettings={{
            timeZone,
            dateFormat,
            timeFormat,
          }}
        >
          <Box
            width="100%"
            height="100%"
            overflow="hidden"
            display="flex"
            flexDirection="column"
          >
            <Stack
              flex={1}
              direction="row"
              width="100%"
              maxWidth="100%"
              overflow="hidden"
              justifyContent="space-between"
              pl={0.5}
              pt={0.2}
              pr={0.2}
            >
              <Box flex={1} maxWidth="100%" overflow="hidden" className="custom-page">
                <Outlet />
              </Box>
            </Stack>
          </Box>
        </FreyrLibraryProvider>
      </Suspense>
    </ThemeProvider>
  );
};

export default Layouts;
